import type { CSSProperties } from "react";
import { coloringApi } from "./api.js";
import { useColoring } from "./ColoringContext.js";

function rgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return `rgba(255,255,255,${alpha})`;
  const n = Number.parseInt(m[1]!, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/**
 * One cover side: the artwork with the title/author/back text typeset on top in
 * crisp type (never baked into the AI image). Sizes are proportional to the cover
 * width (container query units) and mirror the PDF exporter's layout.
 */
export function CoverSheet({ side, variantId, className }: { side: "front" | "back"; variantId: string | null; className?: string }) {
  const { projectId, state } = useColoring();
  const { project, book } = state;
  const c = book.cover;
  const author = c.author || project.author || "";
  const plate = (alpha: number): CSSProperties => (c.titlePlate ? { background: rgba(c.backgroundColor, alpha) } : {});

  return (
    <div className={`cover-sheet ${className ?? ""}`} style={{ aspectRatio: `${book.trimWidth} / ${book.trimHeight}`, background: c.backgroundColor }}>
      {variantId && <img className="cover-sheet-art" src={coloringApi.imageUrl(projectId, variantId)} alt={`${side} cover artwork`} />}
      {side === "front" ? (
        <>
          <div className="cover-title-block" style={plate(0.78)}>
            <div className="cover-title" style={{ color: c.titleColor }}>{project.title}</div>
            {project.subtitle && <div className="cover-subtitle" style={{ color: c.subtitleColor }}>{project.subtitle}</div>}
          </div>
          {author && (
            <div className="cover-author-wrap">
              <span className="cover-author" style={{ color: c.titleColor, ...plate(0.78) }}>{author}</span>
            </div>
          )}
        </>
      ) : (
        c.backText.trim() && (
          <div className="cover-back-block" style={{ ...plate(0.86), color: c.textColor }}>
            {c.backText}
          </div>
        )
      )}
      {!variantId && <div className="cover-sheet-empty">No {side} artwork yet</div>}
    </div>
  );
}
